/** Batch "Manage tags" dialog for the Tours grid (a custom `dialog(ctx)`
 *  batch action). Mirrors the Bookings ManageTagsDialog.
 *
 *  A run is an ordered list of BLOCKS; each block is one operation:
 *   - add     — add the chosen tags (invert = every tag in the category EXCEPT them).
 *   - replace — clear the category on each tour, then add the chosen tags.
 *   - remove  — remove the chosen tags (leave the tag list EMPTY to remove the whole
 *               category; invert = every tag EXCEPT the chosen ones).
 *
 *  Tags are tenant-scoped, so each block picks a source tenant + category + tag(s).
 *  Tenant here is chosen with the tree-based `<TenantPicker>`; the category/tag
 *  options come from the tours picker (`toursApi.picker`), and the `tags` picker
 *  is narrowed to the block's chosen category code. When a block's selection may
 *  span tenants (`detectCrossTenant`, the same heuristic SetLaunchPolicyDialog
 *  uses on this grid) a `CrossTenantStrategyField` offers the tiers the op
 *  supports (add/replace can create the twin; remove never creates). Submit fires
 *  the generic `/tours/batch/` `manage_tags` dispatch via `ctx.runStandard`; the
 *  server validates the blocks, resolves each tag to the tour's own tenant, and is
 *  authoritative (a 400 surfaces as a snackbar). "Dry run" sends the same payload
 *  with `dry_run: true` — the task logs what it WOULD do and writes nothing.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { PickerOption } from '@/api/tours';
import * as toursApi from '@/api/tours';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { CrossTenantStrategyField } from '@/components/DataGrid/CrossTenantStrategyField';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { HelpLink } from '@/components/HelpLink';

type TagOp = 'add' | 'replace' | 'remove';

/** Add/replace can match OR create the twin in each tenant; remove must never
 *  create a tag it is about to remove, so `match_or_create` is withheld. */
const ADD_STRATEGIES = ['ignore_others', 'match_or_ignore', 'match_or_create'];
const REMOVE_STRATEGIES = ['ignore_others', 'match_or_ignore'];

const OP_LABEL: Record<TagOp, string> = { add: 'Add', replace: 'Replace', remove: 'Remove' };

interface Block {
  op: TagOp;
  tenant: TenantPickerValue | null;
  category: PickerOption | null;
  tags: PickerOption[];
  invert: boolean;
  strategy: string | null;
}

function emptyBlock(gridTenantId: number | null): Block {
  return {
    op: 'add',
    tenant: gridTenantId != null ? { id: gridTenantId, name: '' } : null,
    category: null,
    tags: [],
    invert: false,
    strategy: null,
  };
}

interface Props {
  ctx: BatchActionContext;
  /** The grid's current tenant filter (null when unfiltered). Seeds each new
   *  block's source tenant and (like SetLaunchPolicyDialog) is the only input to
   *  the cross-tenant heuristic — the tours grid does not load per-row tenant
   *  ids, so the server stays authoritative for the ids-mode case. */
  gridTenantId: number | null;
}

export function ManageTagsDialog({ ctx, gridTenantId }: Props) {
  const [blocks, setBlocks] = useState<Block[]>([emptyBlock(gridTenantId)]);
  const [description, setDescription] = useState('');
  const [applySideEffects, setApplySideEffects] = useState(true);

  /** A block spans tenants when its source tenant differs from the target rows.
   *  Mirrors SetLaunchPolicyDialog: the picker returns only the source tenant's
   *  own tags, so the value's tenant is the source tenant. */
  const isCrossTenant = (b: Block): boolean =>
    b.tenant?.id != null &&
    detectCrossTenant(b.tenant.id, ctx.selection, { gridTenantFilter: gridTenantId });

  const blockValid = (b: Block): boolean => {
    if (!b.tenant || !b.category) return false;
    if (b.tags.length === 0 && b.op !== 'remove') return false;
    if (isCrossTenant(b) && !b.strategy) return false;
    return true;
  };
  const canSubmit = blocks.length > 0 && blocks.every(blockValid);

  const patchBlock = (i: number, patch: Partial<Block>) =>
    setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const addBlock = () => setBlocks((bs) => [...bs, emptyBlock(gridTenantId)]);
  const removeBlock = (i: number) => setBlocks((bs) => bs.filter((_, j) => j !== i));

  const submit = (dryRun: boolean) => {
    if (!canSubmit) return;
    const payloadBlocks = blocks.map((b) => ({
      op: b.op,
      tenant_id: b.tenant?.id ?? null,
      category_id: b.category?.id,
      tag_ids: b.tags.map((t) => t.id),
      invert: b.op === 'replace' ? false : b.invert,
      ...(isCrossTenant(b) && b.strategy ? { cross_tenant_strategy: b.strategy } : {}),
    }));
    void ctx.runStandard({
      blocks: payloadBlocks,
      dry_run: dryRun,
      apply_side_effects: applySideEffects,
      description: description.trim() || undefined,
    });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="md">
      <DialogTitle>
        Manage tags <HelpLink story="manage-tags-batch" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Apply one or more tag operations to {ctx.count.toLocaleString()} tour(s). This runs in
            the background.
          </Typography>

          <Stack spacing={1.5}>
            {blocks.map((b, i) => (
              <BlockCard
                key={i}
                index={i}
                value={b}
                crossTenant={Boolean(isCrossTenant(b))}
                canRemove={blocks.length > 1}
                onChange={(patch) => patchBlock(i, patch)}
                onRemove={() => removeBlock(i)}
              />
            ))}
          </Stack>
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addBlock}>
              Add operation
            </Button>
          </Box>

          <TextField
            label="Description (optional)"
            size="small"
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Recorded on the audit trail for every changed tour."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={applySideEffects}
                onChange={(e) => setApplySideEffects(e.target.checked)}
              />
            }
            label="Refresh caches (recommended)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Refreshes each tour&apos;s edit cache so the dashboard reflects the change. Uncheck only
            to change tag rows without that refresh.
          </Typography>

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} tours.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="outlined" disabled={!canSubmit} onClick={() => submit(true)}>
          Dry run (preview)
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => submit(false)}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// One operation block. Op + source tenant + category + tag(s), with an
// optional invert (not for replace) and a cross-tenant strategy when needed.
// ---------------------------------------------------------------------------

interface BlockCardProps {
  index: number;
  value: Block;
  crossTenant: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<Block>) => void;
  onRemove: () => void;
}

function BlockCard({ index, value, crossTenant, canRemove, onChange, onRemove }: BlockCardProps) {
  const tenantId = value.tenant?.id ?? null;
  const isRemove = value.op === 'remove';
  const allowsInvert = value.op !== 'replace';

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Operation {index + 1}
        </Typography>
        {canRemove && (
          <IconButton size="small" aria-label="Remove operation" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Operation"
            size="small"
            value={value.op}
            onChange={(e) => {
              const op = e.target.value as TagOp;
              // Switching op invalidates the chosen strategy tier and (for replace)
              // the invert flag.
              onChange({ op, strategy: null, invert: op === 'replace' ? false : value.invert });
            }}
            sx={{ width: { xs: '100%', sm: 160 } }}
          >
            {(['add', 'replace', 'remove'] as TagOp[]).map((op) => (
              <MenuItem key={op} value={op}>
                {OP_LABEL[op]}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flexGrow: 1 }}>
            <TenantPicker
              value={value.tenant}
              onChange={(t) => onChange({ tenant: t, category: null, tags: [], strategy: null })}
            />
          </Box>
        </Stack>

        <RemoteAutocomplete<PickerOption>
          label="Tag category"
          size="small"
          fetchOnEmpty
          value={value.category}
          getOptionLabel={(c) => c.name ?? c.code ?? `#${c.id}`}
          getOptionKey={(c) => c.id}
          queryFn={async (q) =>
            tenantId == null ? [] : (await toursApi.picker(tenantId, 'tag_categories', q)).results
          }
          onChange={(v) => onChange({ category: v as PickerOption | null, tags: [] })}
        />

        <RemoteAutocomplete<PickerOption>
          label={isRemove ? 'Tags (optional)' : 'Tags'}
          size="small"
          multiple
          fetchOnEmpty
          value={value.tags}
          getOptionLabel={(t) => t.name ?? `#${t.id}`}
          getOptionKey={(t) => t.id}
          queryFn={async (q) =>
            tenantId == null
              ? []
              : (await toursApi.picker(tenantId, 'tags', q, value.category?.code)).results
          }
          onChange={(v) => onChange({ tags: (v as PickerOption[]) ?? [] })}
        />
        {isRemove && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Leave empty to remove every tag in the category.
          </Typography>
        )}

        {allowsInvert && (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.invert}
                onChange={(e) => onChange({ invert: e.target.checked })}
              />
            }
            label="All tags in the category except the selected ones"
          />
        )}

        {crossTenant && (
          <CrossTenantStrategyField
            value={value.strategy}
            onChange={(s) => onChange({ strategy: s })}
            allowedStrategies={isRemove ? REMOVE_STRATEGIES : ADD_STRATEGIES}
            noun="tag"
          />
        )}
      </Stack>
    </Paper>
  );
}
